// =============================================
// NodesTab — ONLY node management (generate, batch, edit, demand badges)
// =============================================

import { useState } from "react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Sprout, Zap, ArrowRight, Search, Loader2, Download } from "lucide-react";
import { NodeSheet } from "./NodeSheet";
import { DemandBadge } from "./DemandBadge";
import { LiveIndicator } from "@/components/ui/LiveIndicator";
import { useThematicSeeds } from "@/hooks/useThematicSeeds";
import { useThematicNodes } from "@/hooks/useThematicNodes";
import { useMapGenerator } from "@/hooks/useMapGenerator";
import { useWorkerJob } from "@/hooks/useWorkerJob";
import { useToast } from "@/hooks/use-toast";
import { lightPass, type ValidatedNodePreview } from "@/services/validation/reverseSerpValidator";
import { saveSerpResultsToDb } from "@/services/validation/serpResultsSaver";
import { isDataForSEOConfigured, sanitizeKeyword } from "@/services/dataForSeoService";
import { supabase } from "@/integrations/supabase/client";
import { startLogRun, endLogRun, logPublic } from "@/services/contentEngine/contentLogger";
import type {
  ThematicNodeWithEntity, DocumentRole, NodeStatus, NodePreview, BatchNodeResult, DemandTier,
} from "@/types/thematicMaps";

import type { ThematicMap } from "@/types/thematicMaps";

interface NodesTabProps {
  mapId: string;
  map: ThematicMap;
  onNext?: () => void;
}

const ROLE_SHORT: Record<DocumentRole, string> = {
  definition: "DEF", how_to: "HT", comparison: "CMP", troubleshooting: "TSH",
  glossary: "GLO", history: "HIS", commercial: "COM", protocol: "PRO", guide: "GUI",
};
const STATUS_COLORS: Record<NodeStatus, string> = {
  planned: "bg-muted text-muted-foreground", draft: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  published: "bg-green-500/15 text-green-700 dark:text-green-400", refresh: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  deprecated: "bg-red-500/15 text-red-700 dark:text-red-400",
};

export function NodesTab({ mapId, map, onNext }: NodesTabProps) {
  const { seeds } = useThematicSeeds(mapId);
  const { nodes, createNodesBulk, refreshNodes, isLive: nodesLive } = useThematicNodes(mapId);
  const { generating, runBatchNodeGeneration } = useMapGenerator(mapId);
  const { job: workerJob } = useWorkerJob(mapId, "map_nodes");
  const { job: workerBatchJob } = useWorkerJob(mapId, "map_nodes_batch");

  // P-C (faza P): hide pillar nodes z widoku NodesTab — pillary maja wlasna sekcje
  // w SroStructurePanel (kropka "Pillar Page"). Hook useThematicNodes NIE zmieniamy
  // (SroStructurePanel:82 + inne miejsca polegaja na pelnej liscie).
  // Pillar NIE idzie do SERP validation, NIE do "Waliduj SERP", NIE liczy sie w "X nodes" counter.
  const spokeNodes = nodes.filter((n) => (n as { document_role?: string }).document_role !== "pillar");

  const [selectedNode, setSelectedNode] = useState<ThematicNodeWithEntity | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [savingPreview, setSavingPreview] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; seedName: string } | null>(null);
  const [serpValidating, setSerpValidating] = useState(false);
  const [serpResults, setSerpResults] = useState<ValidatedNodePreview[] | null>(null);
  const { toast } = useToast();

  const seedsWithoutNodes = seeds.filter((s) => spokeNodes.filter((n) => n.seed_id === s.id).length === 0);
  const getNodesForSeed = (seedId: string) => spokeNodes.filter((n) => n.seed_id === seedId);

  const handleNodeClick = (node: ThematicNodeWithEntity) => {
    setSelectedNode(node);
    setSheetOpen(true);
  };

  // Save SERP validation results to DB — deleguje do service (STOP-C)
  const [serpSaving, setSerpSaving] = useState(false);
  const handleSaveSerpResults = async () => {
    if (!serpResults || serpResults.length === 0) return;
    setSerpSaving(true);

    const outcome = await saveSerpResultsToDb(mapId, serpResults, { nodes });

    await refreshNodes();
    setSerpSaving(false);

    if (outcome.failed > 0) {
      toast({
        title: "Częściowy zapis",
        description: `${outcome.updated} zapisanych, ${outcome.failed} błędów${outcome.firstError ? ` (${outcome.firstError})` : ""}`,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Zapisano",
        description: `${outcome.updated} nodes zaktualizowanych z danymi SERP`,
      });
    }
  };

  // DRAFT FLOW: Wygenerowane nody lecą od razu do DB z `node_status='draft'`.
  // User zamknie karte → drafty czekaja w bazie. Accept → UPDATE 'planned'.
  // Per-seed generation usuniety 2026-05-26 — LLM dostawal existingNodes per-seed
  // jako kontekst, produkowal duplicates, wszystkie filtered przez unique constraint.
  // Tylko BULK path (handleBatchGenerateAll) jest dostepny — operuje na seeds bez nodow.

  const handleBatchGenerateAll = async () => {
    const results = await runBatchNodeGeneration(
      map,
      seedsWithoutNodes,
      nodes,
      (current, total, seedName) => {
        setBatchProgress({ current, total, seedName });
      },
    );
    setBatchProgress(null);
    // Zapisz wszystko od razu jako draft — survive reload
    if (results.length > 0) {
      setSavingPreview(true);
      for (const result of results) {
        await createNodesBulk(
          result.nodes.map((n, i) => ({
            seed_id: result.seedId,
            title: n.title,
            slug: n.slug,
            document_role: n.document_role,
            primary_intent: n.primary_intent,
            primary_entity_raw: n.primary_entity,
            required_entities_raw: n.required_entities,
            primary_entity_id: null,
            required_entity_ids: [],
            canonical_question: n.canonical_question,
            canonical_question_hash: n.canonical_question_hash,
            node_status: "draft" as const,
            map_version: "v1",
            sort_order: i,
          })),
        );
      }
      setSavingPreview(false);
    }
  };

  // Akceptacja/odrzucenie draftow — operuje bezposrednio na DB
  const [processingDraft, setProcessingDraft] = useState(false);

  const handleAcceptDraftsForSeed = async (seedId: string) => {
    const seedDrafts = spokeNodes.filter((n) => n.seed_id === seedId && n.node_status === "draft");
    if (seedDrafts.length === 0) return;
    setProcessingDraft(true);
    try {
      const { error } = await (supabase as any)
        .from("thematic_nodes")
        .update({ node_status: "planned" })
        .in("id", seedDrafts.map((n) => n.id));
      if (error) throw error;
      await refreshNodes();
      toast({ title: "Zaakceptowane", description: `${seedDrafts.length} nodow przeniesiono do 'planned'` });
    } catch (err) {
      toast({ title: "Blad", description: err instanceof Error ? err.message : "nieznany", variant: "destructive" });
    } finally {
      setProcessingDraft(false);
    }
  };

  const handleRejectDraftsForSeed = async (seedId: string) => {
    const seedDrafts = spokeNodes.filter((n) => n.seed_id === seedId && n.node_status === "draft");
    if (seedDrafts.length === 0) return;
    if (!confirm(`Odrzucic ${seedDrafts.length} draft nodow tego seeda?`)) return;
    setProcessingDraft(true);
    try {
      const { error } = await (supabase as any)
        .from("thematic_nodes")
        .delete()
        .in("id", seedDrafts.map((n) => n.id));
      if (error) throw error;
      await refreshNodes();
      toast({ title: "Odrzucone", description: `${seedDrafts.length} draft nodow usunieto` });
    } catch (err) {
      toast({ title: "Blad", description: err instanceof Error ? err.message : "nieznany", variant: "destructive" });
    } finally {
      setProcessingDraft(false);
    }
  };

  const handleAcceptAllDrafts = async () => {
    const allDraftIds = spokeNodes.filter((n) => n.node_status === "draft").map((n) => n.id);
    if (allDraftIds.length === 0) return;
    setProcessingDraft(true);
    try {
      const { error } = await (supabase as any)
        .from("thematic_nodes")
        .update({ node_status: "planned" })
        .in("id", allDraftIds);
      if (error) throw error;
      await refreshNodes();
      toast({ title: "Zaakceptowane", description: `${allDraftIds.length} draft nodow zaakceptowano` });
    } catch (err) {
      toast({ title: "Blad", description: err instanceof Error ? err.message : "nieznany", variant: "destructive" });
    } finally {
      setProcessingDraft(false);
    }
  };

  const handleRejectAllDrafts = async () => {
    const allDraftIds = spokeNodes.filter((n) => n.node_status === "draft").map((n) => n.id);
    if (allDraftIds.length === 0) return;
    if (!confirm(`Odrzucic ${allDraftIds.length} wszystkich draft nodow?`)) return;
    setProcessingDraft(true);
    try {
      const { error } = await (supabase as any)
        .from("thematic_nodes")
        .delete()
        .in("id", allDraftIds);
      if (error) throw error;
      await refreshNodes();
      toast({ title: "Odrzucone", description: `${allDraftIds.length} draft nodow usunieto` });
    } catch (err) {
      toast({ title: "Blad", description: err instanceof Error ? err.message : "nieznany", variant: "destructive" });
    } finally {
      setProcessingDraft(false);
    }
  };

  // Reverse SERP Validation — on demand
  // P-C: pillar NIE idzie do SERP (uzywamy spokeNodes, NIE pelnej listy z hook)
  const handleSerpValidation = async () => {
    if (spokeNodes.length === 0) return;
    setSerpValidating(true);
    const startTime = Date.now();
    startLogRun(`serp-validation-${mapId}`, { mapId, nodeCount: spokeNodes.length, phase: "reverse_serp_validation" });
    logPublic("SERP", `START — Reverse SERP Validation dla ${spokeNodes.length} nodes (spoke-only, pillar excluded)`);

    try {
      // Convert existing nodes to NodePreview format for lightPass
      const candidates: NodePreview[] = spokeNodes.map((n) => ({
        title: n.title,
        slug: n.slug,
        document_role: n.document_role,
        primary_intent: n.primary_intent || "",
        primary_entity: n.primary_entity_raw || "",
        canonical_question: n.canonical_question,
        required_entities: n.required_entities_raw || [],
        canonical_question_hash: n.canonical_question_hash,
        duplicate_level: "unique" as const,
      }));

      logPublic("SERP", `Kandydaci: ${candidates.map((c) => c.canonical_question).join(", ")}`);

      const language = map.language || "pl";
      const validated = await lightPass(candidates, language, 2616, (step) => {
        logPublic("SERP", step);
      });

      setSerpResults(validated);

      const keeps = validated.filter((n) => n.serp_validation === "keep").length;
      const suggests = validated.filter((n) => n.serp_validation === "suggest").length;
      const rewrites = validated.filter((n) => n.serp_validation === "rewrite").length;
      const rejects = validated.filter((n) => n.serp_validation === "reject").length;

      logPublic("SERP", `WYNIKI: ${keeps} keep, ${suggests} suggest, ${rewrites} rewrite, ${rejects} reject`);

      // Log per-node results
      for (const v of validated) {
        logPublic("SERP", `  [${v.serp_validation}] "${v.canonical_question}" → SV=${v.candidate_sv}, market="${v.best_market_keyword || "-"}" (SV=${v.market_keyword_sv})${v.validated_question ? " → REWRITE: " + v.validated_question : ""}${v.suggested_question ? " → SUGGEST: " + v.suggested_question : ""}`);
      }

      const durationMs = Date.now() - startTime;
      logPublic("SERP", `DONE — ${(durationMs / 1000).toFixed(1)}s`);
      endLogRun(`serp-validation-${mapId}`, { keeps, suggests, rewrites, rejects, durationMs });

      toast({
        title: "Reverse SERP Validation",
        description: `${keeps} OK, ${rewrites} do przepisania, ${rejects} do odrzucenia`,
      });

      // Don't auto-save — user will click "Zapisz wyniki SERP" button
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "SERP validation failed";
      logPublic("SERP", `ERROR: ${errorMsg}`);
      endLogRun(`serp-validation-${mapId}`, { error: errorMsg });
      toast({ title: "Błąd", description: errorMsg, variant: "destructive" });
    } finally {
      setSerpValidating(false);
    }
  };

  if (seeds.length === 0) {
    return (
      <div className="border-2 border-dashed rounded-xl p-12 text-center">
        <Sprout className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
        <p className="text-lg font-medium text-muted-foreground">Najpierw wygeneruj Seeds</p>
        <p className="text-sm text-muted-foreground mt-1">Wróć do kroku Seeds i wygeneruj klastry tematyczne.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-xl font-semibold">Nodes (artykuły)</h2>
          <LiveIndicator isLive={nodesLive} label="Live" className="mt-1" />
        </div>
        <p className="text-sm text-muted-foreground">
          Nodes to konkretne artykuły/strony w ramach każdego seeda. Każdy node ma rolę (DEF, HT, CMP...) i pytanie kanoniczne.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm px-3 py-1">{seeds.length} seeds</Badge>
          <Badge variant="secondary" className="text-sm px-3 py-1">{spokeNodes.length} nodes</Badge>
        </div>
        <div className="flex items-center gap-2">
          {seedsWithoutNodes.length > 0 && (
            <Button variant="default" size="sm" onClick={handleBatchGenerateAll} disabled={generating}>
              <Zap className="mr-1 h-3.5 w-3.5" />
              {batchProgress
                ? `Seed ${batchProgress.current}/${batchProgress.total}...`
                : `Generuj Nodes (${seedsWithoutNodes.length} seeds)`}
            </Button>
          )}
          {spokeNodes.length > 0 && isDataForSEOConfigured() && (
            <Button variant="outline" size="sm" onClick={handleSerpValidation} disabled={serpValidating}>
              {serpValidating ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Search className="mr-1 h-3.5 w-3.5" />}
              {serpValidating ? "Walidacja SERP..." : `Waliduj SERP (${spokeNodes.length})`}
            </Button>
          )}
          {spokeNodes.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => {
              const exportData = spokeNodes.map((n: any) => {
                const sd = n.serp_validation_data || {};
                const ds = n.demand_source || {};
                return {
                  // Node identity
                  title: n.title,
                  slug: n.slug,
                  canonical_question: n.canonical_question,
                  document_role: n.document_role,
                  primary_entity: n.primary_entity_raw,
                  required_entities: n.required_entities_raw,
                  seed_id: n.seed_id,
                  node_status: n.node_status,

                  // Demand metrics (from DB)
                  search_volume: n.search_volume ?? null,
                  cpc: n.cpc ?? null,
                  keyword_difficulty: n.keyword_difficulty ?? null,
                  demand_tier: n.demand_tier ?? null,
                  commercial_score: n.commercial_score ?? null,
                  node_role: n.node_role ?? null,
                  publish_priority: n.publish_priority ?? null,

                  // SERP validation (from demand_source JSONB)
                  serp_validated: ds?.source === "reverse_serp" || n.serp_validated || false,
                  original_question: ds?.original_question ?? n.original_question ?? null,
                  validated_question: ds?.validated_question ?? n.validated_question ?? null,
                  // 3 separated SV + display fallback
                  candidate_sv: ds?.candidate_sv ?? null,
                  market_keyword_sv: ds?.market_keyword_sv ?? null,
                  validated_sv: ds?.validated_sv ?? null,
                  display_sv: ds?.display_sv ?? null,
                  best_market_keyword: ds?.best_market_keyword ?? null,
                  serp_validation: ds?.decision ?? sd.decision ?? null,
                  serp_validation_reason: ds?.reason ?? sd.reason ?? null,
                  validation_confidence: ds?.confidence ?? sd.confidence ?? null,
                  overlap_score: sd.overlap_score ?? null,

                  // Debug: what was sent to DataForSEO
                  query_sent_to_keyword_data: sanitizeKeyword(n.canonical_question || ""),
                  query_sent_to_serp_live: n.canonical_question || "",
                  location_code: 2616,
                  language_code: "pl",

                  // Market data (from demand_source JSONB, fallback to serp_validation_data)
                  market_keywords: ds?.market_keywords ?? sd.market_keywords ?? [],
                  serp_competitors: ds?.top3_competitors ?? sd.top3_competitors ?? [],

                  // Raw JSONB
                  demand_source_raw: n.demand_source ?? null,
                  serp_validation_data_raw: n.serp_validation_data ?? null,
                };
              });
              const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `nodes_export_${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}>
              <Download className="mr-1 h-3.5 w-3.5" />
              Eksport JSON
            </Button>
          )}
        </div>
      </div>

      {/* Batch Progress */}
      {batchProgress && (
        <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3">
          <Zap className="h-4 w-4 text-primary animate-pulse" />
          <div className="flex-1">
            <p className="text-sm font-medium text-primary">Generowanie: Seed {batchProgress.current}/{batchProgress.total}</p>
            <p className="text-xs text-primary/80">{batchProgress.seedName}</p>
          </div>
          <div className="w-32 bg-primary/20 rounded-full h-2">
            <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }} />
          </div>
        </div>
      )}

      {/* SERP Validation Results */}
      {serpResults && (
        <div className="border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Wyniki Reverse SERP Validation</span>
              <span className="text-xs text-muted-foreground">
                {serpResults.filter((n) => n.serp_validation === "keep").length} keep,{" "}
                {serpResults.filter((n) => n.serp_validation === "rewrite").length} rewrite,{" "}
                {serpResults.filter((n) => n.serp_validation === "suggest").length} suggest,{" "}
                {serpResults.filter((n) => n.serp_validation === "reject").length} reject
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="default" size="sm" className="h-7 text-xs" onClick={handleSaveSerpResults} disabled={serpSaving}>
                {serpSaving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}
                {serpSaving ? "Zapisywanie..." : "Zapisz wyniki do DB"}
              </Button>
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setSerpResults(null)}>Zamknij</Button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Node</TableHead>
                <TableHead className="w-[60px]">SV</TableHead>
                <TableHead className="w-[60px]">CPC</TableHead>
                <TableHead className="w-[60px]">KD</TableHead>
                <TableHead className="w-[80px]">Decyzja</TableHead>
                <TableHead>Powód / Sugestia</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {serpResults.map((node, i) => (
                <TableRow key={i} className={
                  node.serp_validation === "reject" ? "bg-red-500/5" :
                  node.serp_validation === "suggest" ? "bg-amber-500/5" :
                  node.serp_validation === "rewrite" ? "bg-violet-500/5" : ""
                }>
                  <TableCell>
                    <p className="text-sm font-medium">{node.title}</p>
                    <p className="text-xs text-muted-foreground">{node.canonical_question}</p>
                    {node.validated_question && (
                      <p className="text-xs text-violet-600 dark:text-violet-400">→ {node.validated_question}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium ${
                      node.search_volume > 100 ? "text-green-600" :
                      node.search_volume > 0 ? "text-amber-600" : "text-red-500"
                    }`}>{node.search_volume}</span>
                  </TableCell>
                  <TableCell className="text-xs">{node.cpc.toFixed(2)}</TableCell>
                  <TableCell className="text-xs">{node.keyword_difficulty}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${
                      node.serp_validation === "keep" ? "bg-green-500/15 text-green-700 dark:text-green-400" :
                      node.serp_validation === "rewrite" ? "bg-violet-500/15 text-violet-700 dark:text-violet-400" :
                      "bg-red-500/15 text-red-600"
                    }`}>{node.serp_validation}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[300px]">{node.serp_validation_reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* WORKER JOB BANNER — single seed generation */}
      {workerJob && (workerJob.status === "queued" || workerJob.status === "in_progress") && (
        <div className="rounded-lg border-2 border-blue-500/50 bg-blue-500/10 p-3 flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-blue-400 animate-spin shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-blue-300">
              Worker: generuje nody
              {workerJob.seedId && (
                <span className="ml-2 text-xs text-muted-foreground">
                  dla seed {seeds.find((s) => s.id === workerJob.seedId)?.name ?? workerJob.seedId.slice(0, 8)}
                </span>
              )}
            </div>
            {workerJob.progressLabel && (
              <div className="text-xs text-muted-foreground truncate mt-0.5">
                {workerJob.progressLabel}
              </div>
            )}
          </div>
          <span className="text-[10px] text-muted-foreground/70 font-mono uppercase tracking-wider shrink-0">
            Job #{workerJob.queueRowId.slice(0, 8)}
          </span>
        </div>
      )}

      {/* WORKER BATCH JOB BANNER — sekwencyjny batch dla wszystkich seedów */}
      {workerBatchJob && (workerBatchJob.status === "queued" || workerBatchJob.status === "in_progress") && (
        <div className="rounded-lg border-2 border-purple-500/50 bg-purple-500/10 p-3 flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-purple-400 animate-spin shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-purple-300">
              Worker: BATCH nody (sekwencyjnie z growing dedup)
            </div>
            {workerBatchJob.progressLabel && (
              <div className="text-xs text-muted-foreground truncate mt-0.5">
                {workerBatchJob.progressLabel}
              </div>
            )}
          </div>
          <span className="text-[10px] text-muted-foreground/70 font-mono uppercase tracking-wider shrink-0">
            Job #{workerBatchJob.queueRowId.slice(0, 8)}
          </span>
        </div>
      )}

      {/* DRAFT NODES PANEL — propozycje zapisane w DB (survive reload) */}
      {spokeNodes.some((n) => n.node_status === "draft") && (
        <DraftNodesPanel
          nodes={spokeNodes}
          seeds={seeds}
          processing={processingDraft}
          onAcceptAll={handleAcceptAllDrafts}
          onRejectAll={handleRejectAllDrafts}
          onAcceptSeed={handleAcceptDraftsForSeed}
          onRejectSeed={handleRejectDraftsForSeed}
        />
      )}

      {/* Seeds with nodes */}
      <Accordion type="multiple" defaultValue={seeds.map((s) => s.id)} className="space-y-2">
        {seeds.map((seed) => {
          const seedNodes = getNodesForSeed(seed.id);
          return (
            <AccordionItem key={seed.id} value={seed.id} className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3 flex-1 text-left">
                  <Sprout className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                  <span className="font-medium">{seed.name}</span>
                  <Badge variant="secondary" className="shrink-0">{seedNodes.length} nodes</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {seedNodes.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">Brak nodes. Kliknij "Generuj Nodes".</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="text-left py-2 px-1 w-6">#</th>
                          <th className="text-left py-2 px-1">Tytuł / Question</th>
                          <th className="text-left py-2 px-1 w-10">Rola</th>
                          <th className="text-left py-2 px-1 w-20">Entity</th>
                          <th className="text-right py-2 px-1 w-14">SV</th>
                          <th className="text-right py-2 px-1 w-14">SV val</th>
                          <th className="text-right py-2 px-1 w-12">CPC</th>
                          <th className="text-right py-2 px-1 w-8">KD</th>
                          <th className="text-center py-2 px-1 w-16">SERP</th>
                          <th className="text-left py-2 px-1 min-w-[180px]">Validated / Market</th>
                          <th className="text-center py-2 px-1 w-10">Popyt</th>
                          <th className="text-center py-2 px-1 w-16">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seedNodes.map((node, idx) => {
                          const n = node as any;
                          const ds = n.demand_source || {};
                          const serpData = n.serp_validation_data;
                          const serpDecision = ds?.decision || serpData?.decision;
                          const validatedQ = ds?.validated_question || n.validated_question;
                          const isValidated = ds?.source === "reverse_serp" || n.serp_validated === true || n.search_volume > 0;
                          const marketKws: any[] = ds?.market_keywords || serpData?.market_keywords || [];
                          const origSV = ds?.display_sv || n.search_volume || ds?.candidate_sv || ds?.market_keyword_sv || 0;
                          const valSV = ds?.validated_sv || 0;
                          const marketSV = ds?.market_keyword_sv || 0;
                          const cpc = n.cpc || 0;
                          const kd = n.keyword_difficulty || 0;

                          return (
                            <tr key={node.id} className="border-b border-border/30 hover:bg-muted/30 cursor-pointer" onClick={() => handleNodeClick(node)}>
                              <td className="py-2 px-1 text-muted-foreground/50">{idx + 1}</td>
                              <td className="py-2 px-1">
                                <p className="font-medium text-sm text-foreground">{node.title}</p>
                                <p className="text-muted-foreground truncate max-w-[350px]">{node.canonical_question}</p>
                              </td>
                              <td className="py-2 px-1">
                                <Badge variant="outline" className="text-[10px] px-1 py-0">{ROLE_SHORT[node.document_role]}</Badge>
                              </td>
                              <td className="py-2 px-1 text-muted-foreground truncate max-w-[80px]">{node.primary_entity_raw || "—"}</td>
                              <td className="py-2 px-1 text-right font-medium">
                                {isValidated ? (
                                  <span className={origSV > 100 ? "text-green-600" : origSV > 0 ? "text-amber-500" : "text-red-500/60"}>
                                    {origSV > 0 ? origSV.toLocaleString() : "0"}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground/40">?</span>
                                )}
                              </td>
                              <td className="py-2 px-1 text-right font-medium">
                                {valSV > 0 ? (
                                  <span className="text-violet-600 dark:text-violet-400">{valSV.toLocaleString()}</span>
                                ) : (
                                  <span className="text-muted-foreground/30">—</span>
                                )}
                              </td>
                              <td className="py-2 px-1 text-right text-muted-foreground">
                                {cpc > 0 ? cpc.toFixed(2) : "—"}
                              </td>
                              <td className="py-2 px-1 text-right text-muted-foreground">
                                {kd > 0 ? kd : "—"}
                              </td>
                              <td className="py-2 px-1 text-center">
                                {serpDecision ? (
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    serpDecision === "keep" ? "bg-green-500/15 text-green-700 dark:text-green-400" :
                                    serpDecision === "rewrite" ? "bg-violet-500/15 text-violet-700 dark:text-violet-400" :
                                    serpDecision === "reject" ? "bg-red-500/15 text-red-600" :
                                    serpDecision === "suggest" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" :
                                    serpDecision === "merge" ? "bg-orange-500/15 text-orange-700" : ""
                                  }`}>{serpDecision}</span>
                                ) : (
                                  <span className="text-muted-foreground/30">—</span>
                                )}
                              </td>
                              <td className="py-2 px-1">
                                {validatedQ && validatedQ !== node.canonical_question && (
                                  <p className="text-violet-600 dark:text-violet-400 truncate max-w-[200px]">→ {validatedQ}</p>
                                )}
                                {marketKws.length > 0 && (
                                  <div className="flex gap-0.5 flex-wrap mt-0.5">
                                    {marketKws.slice(0, 3).map((kw: any, ki: number) => (
                                      <span key={ki} className="text-[9px] px-1 py-0 rounded bg-muted text-muted-foreground whitespace-nowrap">
                                        {kw.keyword?.slice(0, 25)} <span className="text-green-600">{kw.search_volume}</span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {!validatedQ && marketKws.length === 0 && (
                                  <span className="text-muted-foreground/30">—</span>
                                )}
                              </td>
                              <td className="py-2 px-1 text-center">
                                <DemandBadge tier={n.demand_tier as DemandTier || "unknown"} compact />
                              </td>
                              <td className="py-2 px-1 text-center">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[node.node_status]}`}>
                                  {node.node_status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <NodeSheet node={selectedNode} open={sheetOpen} onOpenChange={setSheetOpen} mapId={mapId} />

      {spokeNodes.length > 0 && onNext && (
        <div className="flex justify-end pt-4">
          <Button onClick={onNext} size="lg">
            Następny krok: Encje
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// DraftNodesPanel — propozycje nodow zapisanych jako draft (survive reload)
// =============================================================================
// Pokazuje wszystkie drafty pogrupowane per seed. Accept → UPDATE node_status='planned'.
// Reject → DELETE. User moze zamknac karte po generacji — drafty czekaja w DB.
interface DraftNodesPanelProps {
  nodes: Array<{ id: string; seed_id: string; title: string; node_status: NodeStatus; document_role: DocumentRole }>;
  seeds: Array<{ id: string; name: string }>;
  processing: boolean;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onAcceptSeed: (seedId: string) => void;
  onRejectSeed: (seedId: string) => void;
}

function DraftNodesPanel({
  nodes, seeds, processing,
  onAcceptAll, onRejectAll, onAcceptSeed, onRejectSeed,
}: DraftNodesPanelProps) {
  const drafts = nodes.filter((n) => n.node_status === "draft");
  if (drafts.length === 0) return null;

  // Group by seed
  const bySeeds = new Map<string, typeof drafts>();
  for (const n of drafts) {
    const arr = bySeeds.get(n.seed_id) ?? [];
    arr.push(n);
    bySeeds.set(n.seed_id, arr);
  }

  return (
    <div className="rounded-lg border-2 border-amber-500/40 bg-amber-500/5 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-amber-700 dark:text-amber-400 text-sm uppercase tracking-wide">
            ⚠ Propozycje nodow ({drafts.length}) — czekaja na akceptacje
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Zapisane w bazie jako draft. Mozesz zamknac karte i wrocic pozniej.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRejectAll}
            disabled={processing}
            className="border-red-500/40 text-red-500 hover:bg-red-500/10"
          >
            Odrzuc wszystkie
          </Button>
          <Button
            size="sm"
            onClick={onAcceptAll}
            disabled={processing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Zaakceptuj wszystkie ({drafts.length})
          </Button>
        </div>
      </div>
      <div className="space-y-2">
        {[...bySeeds.entries()].map(([seedId, seedDrafts]) => {
          const seed = seeds.find((s) => s.id === seedId);
          return (
            <div key={seedId} className="bg-background/60 rounded-md border border-amber-500/20 p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <Sprout className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-sm font-semibold">{seed?.name ?? "nieznany seed"}</span>
                  <Badge variant="outline" className="text-[10px]">{seedDrafts.length} draft</Badge>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRejectSeed(seedId)}
                    disabled={processing}
                    className="h-7 px-2 text-[11px] text-red-500 hover:bg-red-500/10"
                  >
                    Odrzuc ({seedDrafts.length})
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onAcceptSeed(seedId)}
                    disabled={processing}
                    className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Zaakceptuj ({seedDrafts.length})
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-[11px]">
                {seedDrafts.slice(0, 6).map((n) => (
                  <div key={n.id} className="flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-muted/30 truncate">
                    <span className="text-[9px] uppercase text-muted-foreground shrink-0 font-mono">
                      {ROLE_SHORT[n.document_role]}
                    </span>
                    <span className="truncate">{n.title}</span>
                  </div>
                ))}
                {seedDrafts.length > 6 && (
                  <div className="text-[10px] text-muted-foreground italic">
                    +{seedDrafts.length - 6} wiecej...
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
